import React from "react"
import { useFieldArray, UseFormMethods, Controller } from "react-hook-form"
import { FormulaireSchemaType } from "../../../lib/formulaireContext/validation/formulaire.schema"
import { Divider, Grid, Paper, Typography, Box, IconButton } from "@material-ui/core"
import DeleteIcon from "@material-ui/icons/Delete"
import AddBoxIcon from "@material-ui/icons/AddBox"
import { FormTextField } from "../Fields/FormTextField"
import { FormMultiCheckboxField } from "../Fields/FormMultiCheckboxField"
import { QualityEnum } from "../../../lib/formulaireContext/validation/formulaire.schema"
import IndeterminateCheckBoxIcon from "@material-ui/icons/IndeterminateCheckBox"

const beneficiaryQualities = [
    { value: QualityEnum.ARTIST, label: "Artiste" },
    { value: QualityEnum.AUTHOR, label: "Auteur" },
    { value: QualityEnum.COMPOSER, label: "Compo." },
    { value: QualityEnum.ARRANGER, label: "Arrangeur" },
    { value: QualityEnum.EDITOR, label: "Éditeur" },
]


export const WorkBeneficiariesSection = ({ workIndex, form }: { workIndex: number, form: UseFormMethods<FormulaireSchemaType> }) => {
    const { control, formState } = form
    
    const fieldArrayResult = useFieldArray({
        control,
        name: `works.[${workIndex}].beneficiaries`
    });
    const { fields, append, remove } = fieldArrayResult;

    const checkErrors = () => {
        form.clearErrors()
        setTimeout(() => {
            form.trigger();
        }, 50)
    }

    const checkAuthorsEditorsManually = () => {
        const currentValues = form.getValues();
        const currentWork = currentValues.works?.[workIndex];
        if (!currentWork) return null;

        const workAuthors = currentWork.beneficiaries?.filter((beneficiary) => 
            beneficiary.qualities?.includes(QualityEnum.AUTHOR)
        ).length || 0;
        
        const workEditors = currentWork.beneficiaries?.filter((beneficiary) => 
            beneficiary.qualities?.includes(QualityEnum.EDITOR)
        ).length || 0;

        const workComposers = currentWork.beneficiaries?.filter((beneficiary) => 
            beneficiary.qualities?.includes(QualityEnum.COMPOSER)
        ).length || 0;

        if ((workAuthors < 1 && workComposers < 1) || workEditors < 1) {
            return "Au moins un auteur/compositeur et un éditeur sont requis pour cette œuvre";
        }
        return null;
    };

    const handleAdd = () => {
        append({
            beneficiary_id: String(fields.length + 1),
            names: "",
            qualities: []
        })
        checkErrors()
    }

    const handleRemoveLastBeneficiary = () => {
        remove(fields.length - 1)
        checkErrors()
    }

    const handleRemoveBeneficiary = (index: number) => {
        remove(index)
        checkErrors();
    }

    return (
        <Paper elevation={2} style={{ padding: 16, marginBottom: 16, marginTop: 16 }}>
            <Typography variant="h6">Ayants droit</Typography>
            <Divider style={{ marginBottom: '20px' }} />
            
            {/* Affichage de l'erreur de validation globale */}
            {(() => {
                let errorMessage: string | null = null;
                
                const workErrors = formState.errors?.works?.[workIndex];
                if (workErrors?.beneficiaries) {
                    const beneficiariesError = workErrors.beneficiaries as any;
                    if (beneficiariesError.message) {
                        errorMessage = beneficiariesError.message;
                    }
                }
                
                if (!errorMessage) {
                    errorMessage = checkAuthorsEditorsManually();
                }
                
                return errorMessage ? (
                    <Typography variant="body2" color="error" style={{ marginBottom: '16px' }}>
                        {errorMessage}
                    </Typography>
                ) : null;
            })()}

            {fields.map((field, index) => (
                <Box key={field.beneficiary_id} mb={4}>
                    {index > 0 && <Divider style={{ margin: '20px 0' }} />}
                    <Grid container spacing={3}>
                        <Controller
                            control={control}
                            name={`works.[${workIndex}].beneficiaries.[${index}].beneficiary_id`}
                            defaultValue=""
                            render={(field) => {
                                return (
                                    <input
                                        type="hidden"
                                        name={`works.[${workIndex}].beneficiaries.[${index}].beneficiary_id`}
                                        defaultValue={field.value}
                                    />
                                )
                            }}
                        />
                        <Grid item xs={6}>
                            <FormTextField
                                label="​Ayant(s) droit de l'œuvre (Nom et Prénom)"
                                name={`works.[${workIndex}].beneficiaries.[${index}].names`}
                                control={control}
                                formState={formState}
                                required={true}
                                onChange={() => checkErrors()} //needed for error display
                            />
                        </Grid>
                        <Grid item xs={5}>
                            <FormMultiCheckboxField
                                label="Qualités"
                                name={`works.[${workIndex}].beneficiaries.[${index}].qualities`}
                                control={control}
                                formState={formState}
                                choices={beneficiaryQualities}
                                required={true}
                                onChange={() => {
                                    checkErrors()
                                }}
                            />
                        </Grid>

                        <Grid item xs={1} >
                            <IconButton
                                style={{ padding: 0, marginLeft: 'auto' }}
                                onClick={() => handleRemoveBeneficiary(index)}
                                size="small"
                                onMouseOver={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent'
                                }}
                            >
                                <DeleteIcon
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.color = 'red'
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.color = 'inherit'
                                    }}
                                />
                            </IconButton>
                        </Grid>
                    </Grid>
                </Box>
            ))}
            {fields.length === 0 && (
                <Box textAlign="center" py={3}>
                    <Typography variant="body1" color="textSecondary">
                        Aucun ayant droit ajouté, cliquez sur le bouton ci-dessous pour ajouter un ayant droit.
                    </Typography>
                    <Typography variant="body1" color="error">
                        Vous devez ajouter au moins un auteur et un éditeur.
                    </Typography>
                </Box>
            )}

            <Box mt={3} display="flex" justifyContent="start">
                <IconButton
                    style={{ padding: 0 }}
                    onClick={handleAdd}
                    onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent'
                    }}
                >
                    <AddBoxIcon />
                </IconButton>

                <IconButton
                    style={{ padding: 0 }}
                    onClick={handleRemoveLastBeneficiary}
                    onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent'
                    }}
                >
                    <IndeterminateCheckBoxIcon />
                </IconButton>
                <p style={{ paddingLeft: 10 }}>
                    Maximum 10 ayants droit
                    <br />
                    (Ceci ne constitue pas une déclaration d'œuvre qui doit être faite par ailleurs)
                </p>
            </Box>
        </Paper>
    )
}